import { useMemo, useRef, useState } from "react";

import ace from "brace";
import "brace/mode/json";
import "brace/theme/github";
import clsx from "clsx";
import { JsonEditor } from "jsoneditor-react";
import { cloneDeep, debounce } from "lodash";
import {
  Builder,
  Query,
  type ImmutableTree,
} from "react-awesome-query-builder";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  RULE_BASED_SEARCH_MATCHER_CONFIG,
  jsonLogicToSearchJsonLogic,
  jsonLogicToSearchTree,
  searchTreeToJsonLogic,
} from "@pages/rules/matcher-config";
import { SecondaryButton } from "@shared/buttons";

import { createEmptyMatcher } from "../utils";

interface Props {
  mainMatcher: any;
  searchMatcher: any;
  onChange: (value: any) => void;
}

export const SearchMatcherEditor = ({
  mainMatcher,
  searchMatcher,
  onChange,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { user } = useUser();

  const [tree, setTree] = useState(() => jsonLogicToSearchTree(searchMatcher));
  const [isRaw, setIsRaw] = useState(false);

  const editorRef = useRef(null);

  const debouncedOnChangeTree = useMemo(
    () =>
      debounce((value: ImmutableTree) => {
        onChange(searchTreeToJsonLogic(value));
      }, 500),
    [onChange],
  );

  const debouncedOnChangeRaw = useMemo(
    () =>
      debounce((value: any) => {
        const toSet = value || createEmptyMatcher();
        try {
          setTree(jsonLogicToSearchTree(toSet));
        } catch (e) {
          return;
        }

        onChange(toSet);
      }, 500),
    [onChange],
  );

  const renderBuilder = useMemo(
    () => (props: any) =>
      (
        <div>
          <div className="query-builder-container">
            <div className="query-builder qb-lite ml-0 mr-0">
              <Builder {...props} />
            </div>
          </div>
        </div>
      ),
    [],
  );

  const outOfSync = useMemo(() => {
    const sanitizedMainMatcher = jsonLogicToSearchJsonLogic(mainMatcher);
    const sanitizedSearchMatcher = jsonLogicToSearchJsonLogic(searchMatcher);

    return (
      JSON.stringify(sanitizedMainMatcher) !==
      JSON.stringify(sanitizedSearchMatcher)
    );
  }, [mainMatcher, searchMatcher]);

  return (
    <div className="border rounded-lg pt-2 pl-3">
      <div className="flex items-center justify-between mr-3">
        <h4 className="m-0 text-lg font-bold">Rule Based Search</h4>
        <div className="flex items-center gap-4">
          {outOfSync && (
            <GenericTooltip
              noInfoCircle
              content="Sync with main rule"
              overlayClassName="pointer-events-none"
              mouseEnterDelay={500}
            >
              <button
                className="text-xs rounded-md h-6 w-6 hover:bg-cosmic-3"
                onClick={() => {
                  const sanitizedMainMAtcher = jsonLogicToSearchJsonLogic(
                    cloneDeep(mainMatcher),
                  );
                  onChange(sanitizedMainMAtcher);
                  setTree(jsonLogicToSearchTree(cloneDeep(mainMatcher)));

                  if (editorRef.current) {
                    const editor = editorRef.current as any;
                    editor.jsonEditor.set(sanitizedMainMAtcher);
                  }
                }}
              >
                <i className="fa-regular fa-rotate text-cosmic"></i>
              </button>
            </GenericTooltip>
          )}
          {user.isAdmin && (
            <>
              {isRaw ? (
                <SecondaryButton
                  className="font-bold flex items-center gap-2"
                  onClick={() => {
                    prettyLogEventPage({
                      domain: "Breakdown v2",
                      action: "onClick",
                      additionalActionInfo:
                        "Change advanced search editor to structured moda",
                    });

                    setIsRaw(false);
                  }}
                >
                  <i className="fa-regular fa-block-quote text-cosmic" />
                  VIEW STRUCTURED
                </SecondaryButton>
              ) : (
                <SecondaryButton
                  className="font-bold flex items-center gap-2"
                  onClick={() => {
                    prettyLogEventPage({
                      domain: "Breakdown v2",
                      action: "onClick",
                      additionalActionInfo:
                        "Change advanced search editor to raw moda",
                    });

                    setIsRaw(true);
                  }}
                >
                  <i className="fa-regular fa-code text-cosmic" />
                  VIEW RAW
                </SecondaryButton>
              )}
            </>
          )}
        </div>
      </div>
      <div className="mt-4">
        {isRaw ? (
          <JsonEditor
            ref={editorRef}
            value={searchMatcher}
            onChange={debouncedOnChangeRaw}
            mode="code"
            ace={ace}
            htmlElementProps={{ style: { height: 500 } }}
          />
        ) : (
          <div className="react-awesome-query-builder">
            <Query
              {...RULE_BASED_SEARCH_MATCHER_CONFIG}
              value={tree}
              onChange={(value) => {
                setTree(value);
                debouncedOnChangeTree(value);
              }}
              renderBuilder={renderBuilder}
            />
          </div>
        )}
      </div>
      <div
        className={clsx(
          "border border-gray rounded-md mr-3 mb-3 bg-sun-2 text-xs py-1 px-2",
          "flex items-center gap-2",
        )}
      >
        <i className="fa-solid fa-warning text-sun+2 text-base"></i>
        <div>
          <strong>Google Campaigns, Facebook Campaigns and Inferred</strong>{" "}
          fields are not supported by rule based search
        </div>
      </div>
    </div>
  );
};
